/**
 * devices.ts — the DEVICE catalog + the pure logic Edit Mode devices run on.
 *
 * Research-shaped (UEFN devices: a placeable with configurable properties,
 * events OUT and functions IN, wired by name; Roblox attributes; Yarn/Twine
 * dialogue nodes; Catmull-Rom camera paths; grid A* with inflated obstacles
 * and string-pulled paths). Everything here is pure (no three/DOM) so the
 * harness can prove it: the per-kind field schema drives the editor form, the
 * config parser supplies defaults, and the evaluators (dialogue, schedule,
 * camera spline, pathfinding) are the exact functions the runtime calls.
 *
 * SIGNALS are the wire: a device EMITS `on`/`off`/`enter`/`exit`/`press`
 * signals named after it (topic = `<name>:<event>`), and LISTENS to the topics
 * in its `listen` field — `npc.signal` / `world.signal` from scripts ride the
 * same bus, so NPCs, world scripts and devices all talk to each other.
 */

export type DeviceGroup = 'light' | 'interact' | 'motion' | 'zone' | 'water' | 'audio' | 'cinematic' | 'npc' | 'gameplay';

export interface FieldSpec {
  key: string;
  label: string;
  type: 'number' | 'text' | 'bool' | 'color' | 'select' | 'signal' | 'textarea';
  min?: number; max?: number; step?: number;
  options?: Array<{ v: string; l: string }>;
  hint?: string;
}

export interface DeviceKindDef {
  kind: string;
  label: string;
  group: DeviceGroup;
  hint: string;
  /** Default extent (sx, sy, sz) — meaning per kind (see FieldSpec hints). */
  size: [number, number, number];
  /** Default config values. */
  defaults: Record<string, unknown>;
  fields: FieldSpec[];
  /** Pressing E near it does something (the runtime handles it). */
  interactive?: boolean;
  /** Signals this kind emits (topic suffixes), for the editor's wiring hints. */
  emits?: string[];
}

export const DEVICE_GROUPS: Array<{ key: DeviceGroup; title: string }> = [
  { key: 'light', title: 'Light, FX & signage' },
  { key: 'interact', title: 'Interactables' },
  { key: 'motion', title: 'Physics & motion' },
  { key: 'zone', title: 'Zones & gameplay' },
  { key: 'water', title: 'Terrain & water' },
  { key: 'audio', title: 'Audio' },
  { key: 'cinematic', title: 'Cinematic' },
  { key: 'npc', title: 'NPC' },
  { key: 'gameplay', title: 'Rules' },
];

const LISTEN: FieldSpec = { key: 'listen', label: 'Listen to signal', type: 'signal', hint: 'topic that toggles/triggers this device (e.g. "lever1:on") — blank = none' };
const EMIT: FieldSpec = { key: 'emit', label: 'Emit signal', type: 'signal', hint: 'topic sent when this device fires (default "<name>:on")' };

export const DEVICE_KINDS: DeviceKindDef[] = [
  { kind: 'light', label: 'Light', group: 'light', hint: 'A point or spot light', size: [0, 0, 0], emits: [],
    defaults: { type: 'point', color: '#ffd9a0', intensity: 12, range: 14, angle: 45, flicker: 0, castShadow: false, onByDefault: true },
    fields: [
      { key: 'type', label: 'Type', type: 'select', options: [{ v: 'point', l: 'point' }, { v: 'spot', l: 'spot (down the yaw)' }] },
      { key: 'color', label: 'Color', type: 'color' },
      { key: 'intensity', label: 'Intensity', type: 'number', min: 0, max: 200, step: 1 },
      { key: 'range', label: 'Range (m)', type: 'number', min: 1, max: 120, step: 1 },
      { key: 'angle', label: 'Spot angle °', type: 'number', min: 5, max: 90, step: 1 },
      { key: 'flicker', label: 'Flicker 0..1', type: 'number', min: 0, max: 1, step: 0.05 },
      { key: 'castShadow', label: 'Casts shadows (costly)', type: 'bool' },
      { key: 'onByDefault', label: 'On by default', type: 'bool' },
      LISTEN,
    ] },
  { kind: 'particles', label: 'Particle emitter', group: 'light', hint: 'Smoke, sparks, fireflies, rain, fountain, embers', size: [1, 1, 1],
    defaults: { preset: 'fireflies', rate: 40, color: '#ffd27f', size: 0.12, spread: 1.5, speed: 0.6, life: 3, onByDefault: true },
    fields: [
      { key: 'preset', label: 'Preset', type: 'select', options: ['fireflies', 'smoke', 'sparks', 'embers', 'rain', 'fountain', 'snow', 'dust'].map((p) => ({ v: p, l: p })) },
      { key: 'rate', label: 'Particles / s', type: 'number', min: 1, max: 400, step: 1 },
      { key: 'color', label: 'Color', type: 'color' },
      { key: 'size', label: 'Size (m)', type: 'number', min: 0.02, max: 2, step: 0.02 },
      { key: 'spread', label: 'Spread (m)', type: 'number', min: 0.1, max: 30, step: 0.1 },
      { key: 'speed', label: 'Speed (m/s)', type: 'number', min: 0, max: 20, step: 0.1 },
      { key: 'life', label: 'Life (s)', type: 'number', min: 0.2, max: 20, step: 0.1 },
      { key: 'onByDefault', label: 'On by default', type: 'bool' },
      LISTEN,
    ] },
  { kind: 'sign', label: 'Sign / text', group: 'light', hint: 'Floating 3D text or a board', size: [2, 1, 0],
    defaults: { text: 'Welcome', style: 'board', color: '#eaf6ff', bg: '#0a0e16', scale: 1, billboard: true },
    fields: [
      { key: 'text', label: 'Text', type: 'textarea', hint: 'up to 4 lines' },
      { key: 'style', label: 'Style', type: 'select', options: [{ v: 'board', l: 'board' }, { v: 'hologram', l: 'hologram' }, { v: 'plain', l: 'plain text' }] },
      { key: 'color', label: 'Text color', type: 'color' },
      { key: 'bg', label: 'Board color', type: 'color' },
      { key: 'scale', label: 'Scale', type: 'number', min: 0.25, max: 8, step: 0.25 },
      { key: 'billboard', label: 'Always face the viewer', type: 'bool' },
      LISTEN,
    ] },
  { kind: 'door', label: 'Door', group: 'interact', hint: 'Opens on E or on a signal (slide or swing)', size: [2, 3, 0.2], interactive: true, emits: ['open', 'close'],
    defaults: { motion: 'swing', color: '#8a6a4a', speed: 1.2, autoClose: 4, locked: false, keyTopic: '', emit: '' },
    fields: [
      { key: 'motion', label: 'Motion', type: 'select', options: [{ v: 'swing', l: 'swing' }, { v: 'slide', l: 'slide sideways' }, { v: 'lift', l: 'lift up' }] },
      { key: 'color', label: 'Color', type: 'color' },
      { key: 'speed', label: 'Speed (s)', type: 'number', min: 0.2, max: 6, step: 0.1 },
      { key: 'autoClose', label: 'Auto-close after (s, 0 = stays)', type: 'number', min: 0, max: 120, step: 0.5 },
      { key: 'locked', label: 'Locked (needs the key signal)', type: 'bool' },
      { key: 'keyTopic', label: 'Key signal', type: 'signal', hint: 'a signal that unlocks it, e.g. "lever1:on"' },
      EMIT, LISTEN,
    ] },
  { kind: 'button', label: 'Button / lever', group: 'interact', hint: 'Press E → sends a signal', size: [0.5, 0.5, 0.5], interactive: true, emits: ['on', 'off', 'press'],
    defaults: { mode: 'toggle', color: '#ff8844', label: 'Press', cooldown: 0.5, emit: '' },
    fields: [
      { key: 'mode', label: 'Mode', type: 'select', options: [{ v: 'toggle', l: 'toggle on/off' }, { v: 'momentary', l: 'press' }] },
      { key: 'label', label: 'Prompt', type: 'text' },
      { key: 'color', label: 'Color', type: 'color' },
      { key: 'cooldown', label: 'Cooldown (s)', type: 'number', min: 0, max: 60, step: 0.5 },
      EMIT,
    ] },
  { kind: 'trigger', label: 'Trigger volume', group: 'interact', hint: 'Fires enter/exit signals when a player walks in', size: [4, 3, 4], emits: ['enter', 'exit'],
    defaults: { once: false, emit: '' },
    fields: [
      { key: 'once', label: 'Only the first time', type: 'bool' },
      EMIT,
    ] },
  { kind: 'teleporter', label: 'Teleporter', group: 'interact', hint: 'E on the pad → a destination (server-moved)', size: [2, 0.2, 2], interactive: true, emits: ['used'],
    defaults: { tx: 0, ty: 0, tz: 0, tyaw: 0, color: '#66ffd0', prompt: 'Teleport' },
    fields: [
      { key: 'tx', label: 'Target X', type: 'number', step: 0.5 },
      { key: 'ty', label: 'Target Y', type: 'number', step: 0.5 },
      { key: 'tz', label: 'Target Z', type: 'number', step: 0.5 },
      { key: 'tyaw', label: 'Target yaw (rad)', type: 'number', step: 0.1 },
      { key: 'prompt', label: 'Prompt', type: 'text' },
      { key: 'color', label: 'Color', type: 'color' },
    ] },
  { kind: 'seat', label: 'Seat', group: 'interact', hint: 'E to sit; any move key stands up', size: [0.6, 0.5, 0.6], interactive: true, emits: ['sit', 'stand'],
    defaults: { color: '#6a5a4a', prompt: 'Sit' },
    fields: [{ key: 'prompt', label: 'Prompt', type: 'text' }, { key: 'color', label: 'Color', type: 'color' }] },
  { kind: 'mover', label: 'Moving platform', group: 'motion', hint: 'A platform that glides back and forth', size: [3, 0.3, 3],
    defaults: { axis: 'y', distance: 4, period: 6, color: '#5a6a7a', pause: 1, onByDefault: true },
    fields: [
      { key: 'axis', label: 'Axis', type: 'select', options: [{ v: 'x', l: 'x' }, { v: 'y', l: 'y (lift)' }, { v: 'z', l: 'z' }] },
      { key: 'distance', label: 'Distance (m)', type: 'number', min: 0.5, max: 60, step: 0.5 },
      { key: 'period', label: 'Round trip (s)', type: 'number', min: 1, max: 60, step: 0.5 },
      { key: 'pause', label: 'Pause at ends (s)', type: 'number', min: 0, max: 20, step: 0.5 },
      { key: 'color', label: 'Color', type: 'color' },
      { key: 'onByDefault', label: 'Moving by default', type: 'bool' },
      LISTEN,
    ] },
  { kind: 'spinner', label: 'Spinner', group: 'motion', hint: 'A beam / disc that rotates', size: [4, 0.3, 0.6],
    defaults: { rpm: 6, color: '#7a5a5a', onByDefault: true },
    fields: [
      { key: 'rpm', label: 'Turns / min', type: 'number', min: 0.5, max: 120, step: 0.5 },
      { key: 'color', label: 'Color', type: 'color' },
      { key: 'onByDefault', label: 'Spinning by default', type: 'bool' },
      LISTEN,
    ] },
  { kind: 'jump_pad', label: 'Jump pad', group: 'motion', hint: 'Launches you up when you step on it', size: [1.5, 0.2, 1.5], emits: ['launch'],
    defaults: { power: 12, color: '#66ff88' },
    fields: [{ key: 'power', label: 'Launch speed (m/s)', type: 'number', min: 4, max: 40, step: 1 }, { key: 'color', label: 'Color', type: 'color' }] },
  { kind: 'gravity_zone', label: 'Gravity zone', group: 'zone', hint: 'Low (or high) gravity while inside', size: [8, 6, 8], emits: ['enter', 'exit'],
    defaults: { gravity: 0.3 },
    fields: [{ key: 'gravity', label: 'Gravity ×', type: 'number', min: 0.05, max: 3, step: 0.05 }] },
  { kind: 'kill_zone', label: 'Kill / respawn zone', group: 'zone', hint: 'Entering respawns you (lava, the void)', size: [6, 4, 6], emits: ['enter'],
    defaults: { message: 'Ouch.' },
    fields: [{ key: 'message', label: 'Message', type: 'text' }] },
  { kind: 'checkpoint', label: 'Checkpoint', group: 'zone', hint: 'Touching it saves your respawn point', size: [2, 0.2, 2], emits: ['reached'],
    defaults: { color: '#ffd166', message: 'Checkpoint reached' },
    fields: [{ key: 'message', label: 'Message', type: 'text' }, { key: 'color', label: 'Color', type: 'color' }] },
  { kind: 'spawn_point', label: 'Spawn point', group: 'gameplay', hint: 'Where visitors arrive (the newest one wins)', size: [1.5, 0.2, 1.5],
    defaults: { color: '#7ef0a0' },
    fields: [{ key: 'color', label: 'Color', type: 'color' }] },
  { kind: 'speaker', label: 'Speaker', group: 'audio', hint: 'Plays a clip on E or on a signal (positional)', size: [0.4, 0.4, 0.4], interactive: true,
    defaults: { assetId: '0', gain: 0.8, loop: false, onByDefault: false, range: 20, prompt: 'Play' },
    fields: [
      { key: 'assetId', label: 'Clip (render_asset id)', type: 'text', hint: 'pick from the Audio tool\'s clip list' },
      { key: 'gain', label: 'Gain', type: 'number', min: 0, max: 1.5, step: 0.05 },
      { key: 'range', label: 'Range (m)', type: 'number', min: 2, max: 100, step: 1 },
      { key: 'loop', label: 'Loop', type: 'bool' },
      { key: 'onByDefault', label: 'Playing by default', type: 'bool' },
      { key: 'prompt', label: 'Prompt (E)', type: 'text' },
      LISTEN,
    ] },
  { kind: 'water', label: 'Water volume', group: 'water', hint: 'A pool / lake surface you can swim under', size: [20, 2, 20],
    defaults: { color: '#2a6f9f', opacity: 0.72, waves: 0.15 },
    fields: [
      { key: 'color', label: 'Color', type: 'color' },
      { key: 'opacity', label: 'Opacity', type: 'number', min: 0.1, max: 1, step: 0.05 },
      { key: 'waves', label: 'Waves (m)', type: 'number', min: 0, max: 1, step: 0.05 },
    ] },
  { kind: 'camera_path', label: 'Cinematic camera path', group: 'cinematic', hint: 'Keyframes the camera flies through (E, signal or script)', size: [0.5, 0.5, 0.5], interactive: true, emits: ['start', 'end'],
    defaults: { keys: [], duration: 8, letterbox: true, fade: true, skippable: true, lockPlayer: true, prompt: 'Watch', listen: '' },
    fields: [
      { key: 'duration', label: 'Duration (s)', type: 'number', min: 1, max: 120, step: 0.5 },
      { key: 'letterbox', label: 'Letterbox bars', type: 'bool' },
      { key: 'fade', label: 'Fade in/out', type: 'bool' },
      { key: 'skippable', label: 'Esc skips', type: 'bool' },
      { key: 'prompt', label: 'Prompt (E)', type: 'text' },
      LISTEN,
    ] },
  { kind: 'path', label: 'NPC path', group: 'npc', hint: 'Waypoints an NPC can follow (npc.follow_path("name"))', size: [0.5, 0.5, 0.5],
    defaults: { points: [], loop: true },
    fields: [{ key: 'loop', label: 'Loop', type: 'bool' }] },
  { kind: 'scoreboard', label: 'Scoreboard', group: 'gameplay', hint: 'Shows a counter driven by signals', size: [2, 1, 0],
    defaults: { title: 'Score', topic: 'score', color: '#ffd166', bg: '#0a0e16' },
    fields: [
      { key: 'title', label: 'Title', type: 'text' },
      { key: 'topic', label: 'Count signal', type: 'signal', hint: 'each signal on this topic adds its payload (default +1)' },
      { key: 'color', label: 'Text color', type: 'color' },
      { key: 'bg', label: 'Board color', type: 'color' },
    ] },
  { kind: 'timer', label: 'Timer', group: 'gameplay', hint: 'Counts down from a start signal, fires when done', size: [1, 1, 0], emits: ['done', 'tick'],
    defaults: { seconds: 60, autoStart: false, listen: '', emit: '', color: '#eaf6ff', bg: '#0a0e16' },
    fields: [
      { key: 'seconds', label: 'Seconds', type: 'number', min: 1, max: 3600, step: 1 },
      { key: 'autoStart', label: 'Starts on its own', type: 'bool' },
      { key: 'listen', label: 'Start signal', type: 'signal' },
      { key: 'emit', label: 'Done signal', type: 'signal', hint: 'default "<name>:done"' },
      { key: 'color', label: 'Text color', type: 'color' },
      { key: 'bg', label: 'Board color', type: 'color' },
    ] },
];

export const DEVICE_BY_KIND = new Map(DEVICE_KINDS.map((d) => [d.kind, d]));

/** Parse a device's config JSON over its kind defaults (unknown kinds get {}). */
export function deviceConfig(kind: string, json: string): Record<string, unknown> {
  const def = DEVICE_BY_KIND.get(kind);
  let raw: Record<string, unknown> = {};
  try { const o = JSON.parse(json || '{}') as unknown; if (o && typeof o === 'object' && !Array.isArray(o)) raw = o as Record<string, unknown>; } catch { /* defaults */ }
  return { ...(def?.defaults ?? {}), ...raw };
}

export const num = (v: unknown, d: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v)) ? Number(v) : d);
export const str = (v: unknown, d = ''): string => (typeof v === 'string' ? v : v === undefined || v === null ? d : String(v));
export const bool = (v: unknown, d = false): boolean => (typeof v === 'boolean' ? v : v === 'true' ? true : v === 'false' ? false : d);

/** Secondary/bookkeeping events that never take the `emit` override (a timer's `emit: "gate:open"` must fire on done, not on every tick). */
export const SECONDARY_EVENTS: ReadonlySet<string> = new Set(['tick', 'exit', 'stand', 'end', 'off', 'closed']);

/** The signal topic a device emits for an event ("<name>:<event>" unless `emit` overrides its PRIMARY event). */
export function emitTopic(name: string, cfg: Record<string, unknown>, event: string): string {
  const custom = str(cfg.emit).trim();
  return custom && !SECONDARY_EVENTS.has(event) ? custom : `${name}:${event}`;
}

// ─── Dialogue trees (Yarn/Twine-shaped, data not code) ───────────────────────

export interface DialogueChoice {
  text: string;
  /** Next node id ('' = end). */
  next: string;
  /** Optional requirement: a memory key that must be truthy (prefix ! negates). */
  require?: string;
  /** Optional effects: "mem key=value", "state label", "signal topic payload", "say text". */
  actions?: string[];
}
export interface DialogueNode {
  id: string;
  text: string;
  choices: DialogueChoice[];
  /** Actions run when the node is shown. */
  actions?: string[];
}
export interface DialogueTree { start: string; nodes: DialogueNode[] }

export function parseDialogue(raw: unknown): DialogueTree | null {
  if (!raw || typeof raw !== 'object') return null;
  const t = raw as Partial<DialogueTree>;
  if (!Array.isArray(t.nodes) || t.nodes.length === 0) return null;
  const nodes: DialogueNode[] = t.nodes.filter((n) => n && typeof n === 'object' && typeof (n as DialogueNode).id === 'string').map((n) => ({
    id: String((n as DialogueNode).id).slice(0, 32),
    text: str((n as DialogueNode).text).slice(0, 400),
    actions: Array.isArray((n as DialogueNode).actions) ? ((n as DialogueNode).actions as unknown[]).map((a) => String(a).slice(0, 120)).slice(0, 8) : [],
    choices: (Array.isArray((n as DialogueNode).choices) ? ((n as DialogueNode).choices as unknown[]) : []).slice(0, 6).map((c) => {
      const cc = (c ?? {}) as Partial<DialogueChoice>;
      return { text: str(cc.text).slice(0, 80), next: str(cc.next).slice(0, 32), require: cc.require ? String(cc.require).slice(0, 40) : undefined,
        actions: Array.isArray(cc.actions) ? (cc.actions as unknown[]).map((a) => String(a).slice(0, 120)).slice(0, 8) : undefined };
    }),
  }));
  const start = typeof t.start === 'string' && nodes.some((n) => n.id === t.start) ? t.start : nodes[0].id;
  return { start, nodes };
}

export function dialogueNode(tree: DialogueTree, id: string): DialogueNode | null { return tree.nodes.find((n) => n.id === id) ?? null; }

/** Choices visible to a player given their memory (require "key" / "!key"). */
export function visibleChoices(node: DialogueNode, memory: (key: string) => unknown): DialogueChoice[] {
  return node.choices.filter((c) => {
    if (!c.require) return true;
    const neg = c.require.startsWith('!');
    const key = neg ? c.require.slice(1) : c.require;
    const v = memory(key);
    const truthy = !(v === undefined || v === null || v === '' || v === 0 || v === false);
    return neg ? !truthy : truthy;
  });
}

export interface ParsedAction { op: 'mem' | 'state' | 'signal' | 'say' | 'emote' | 'goto' | 'unknown'; key: string; value: string }

/** "mem stage=given" · "state happy" · "signal quest_done bob" · "say Thanks!" · "emote wave" · "goto 10 20" */
export function parseAction(a: string): ParsedAction {
  const m = /^\s*(mem|state|signal|say|emote|goto)\s+(.*)$/s.exec(a);
  if (!m) return { op: 'unknown', key: '', value: a };
  const op = m[1] as ParsedAction['op'];
  const rest = m[2].trim();
  if (op === 'mem') { const eq = rest.indexOf('='); return eq < 0 ? { op, key: rest, value: '1' } : { op, key: rest.slice(0, eq).trim(), value: rest.slice(eq + 1).trim() }; }
  if (op === 'signal') { const sp = rest.indexOf(' '); return sp < 0 ? { op, key: rest, value: '' } : { op, key: rest.slice(0, sp), value: rest.slice(sp + 1).trim() }; }
  return { op, key: '', value: rest };
}

/** A tiny starter tree for the editor's "New dialogue" button. */
export const SAMPLE_DIALOGUE: DialogueTree = {
  start: 'hello',
  nodes: [
    { id: 'hello', text: 'Well met, traveler. Looking for work?', choices: [
      { text: 'What kind of work?', next: 'job' },
      { text: 'I already helped you.', next: 'thanks', require: 'helped' },
      { text: 'Not today.', next: '' },
    ] },
    { id: 'job', text: 'The lamp by the gate went dark. Switch it back on and come see me.', actions: ['mem quest=lamp', 'state hopeful'], choices: [
      { text: 'On it.', next: '', actions: ['say Good luck out there.'] },
    ] },
    { id: 'thanks', text: 'You did — and I remember it.', actions: ['emote bow'], choices: [{ text: 'Any time.', next: '' }] },
  ],
};

// ─── NPC schedules ───────────────────────────────────────────────────────────

export interface ScheduleEntry { hour: number; action: 'goto' | 'home' | 'wander' | 'state' | 'path' | 'say'; x?: number; z?: number; value?: string }

export function parseSchedule(raw: unknown): ScheduleEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: ScheduleEntry[] = [];
  for (const e of raw.slice(0, 24)) {
    if (!e || typeof e !== 'object') continue;
    const r = e as Partial<ScheduleEntry>;
    const hour = num(r.hour, -1);
    if (hour < 0 || hour > 23) continue;
    const action = ['goto', 'home', 'wander', 'state', 'path', 'say'].includes(String(r.action)) ? r.action as ScheduleEntry['action'] : 'state';
    out.push({ hour: Math.floor(hour), action, x: r.x === undefined ? undefined : num(r.x, 0), z: r.z === undefined ? undefined : num(r.z, 0), value: r.value === undefined ? undefined : str(r.value).slice(0, 80) });
  }
  return out.sort((a, b) => a.hour - b.hour);
}

/** The entry in force at `hour` (the latest whose hour ≤ now; wraps to the last of the day). */
export function scheduleEntryFor(list: ScheduleEntry[], hour: number): ScheduleEntry | null {
  if (list.length === 0) return null;
  let cur: ScheduleEntry | null = null;
  for (const e of list) if (e.hour <= hour) cur = e;
  return cur ?? list[list.length - 1];
}

// ─── Camera paths (Catmull-Rom through every keyframe) ───────────────────────

export interface CameraKey { x: number; y: number; z: number; yaw: number; pitch: number }

export function parseCameraKeys(raw: unknown): CameraKey[] {
  if (!Array.isArray(raw)) return [];
  return raw.slice(0, 64).map((k) => { const r = (k ?? {}) as Partial<CameraKey>; return { x: num(r.x, 0), y: num(r.y, 0), z: num(r.z, 0), yaw: num(r.yaw, 0), pitch: num(r.pitch, 0) }; });
}

function cr(p0: number, p1: number, p2: number, p3: number, t: number): number {
  const t2 = t * t, t3 = t2 * t;
  return 0.5 * ((2 * p1) + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 + (-p0 + 3 * p1 - 3 * p2 + p3) * t3);
}
function unwrapYaw(from: number, to: number): number {
  let d = to - from;
  if (d > 1e6 || d < -1e6) d = 0; // no heading is a million radians: a 3e38 f32 never shrinks under −2π and the loop below spun forever (bug sweep 2026-09-22)
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return from + d;
}

/** Sample the path at u ∈ [0,1] (uniform by keyframe; hits every key exactly; yaw takes the short way round). */
export function sampleCameraPath(keys: CameraKey[], u: number): CameraKey {
  if (keys.length === 0) return { x: 0, y: 0, z: 0, yaw: 0, pitch: 0 };
  if (keys.length === 1) return { ...keys[0] };
  const segs = keys.length - 1;
  const s = Math.min(segs - 1e-9, Math.max(0, u * segs));
  const i = Math.floor(s), t = s - i;
  const k = (j: number): CameraKey => keys[Math.min(keys.length - 1, Math.max(0, j))];
  const p0 = k(i - 1), p1 = k(i), p2 = k(i + 1), p3 = k(i + 2);
  // Unwrap yaws around p1 so the spline never spins the long way.
  const y1 = p1.yaw, y0 = unwrapYaw(y1, p0.yaw), y2 = unwrapYaw(y1, p2.yaw), y3 = unwrapYaw(y2, p3.yaw);
  return {
    x: cr(p0.x, p1.x, p2.x, p3.x, t), y: cr(p0.y, p1.y, p2.y, p3.y, t), z: cr(p0.z, p1.z, p2.z, p3.z, t),
    yaw: cr(y0, y1, y2, y3, t), pitch: cr(p0.pitch, p1.pitch, p2.pitch, p3.pitch, t),
  };
}

/** Ease-in-out so a flight starts and ends softly. */
export const easeInOut = (u: number): number => (u < 0.5 ? 2 * u * u : 1 - Math.pow(-2 * u + 2, 2) / 2);

// ─── Grid A* (inflated obstacles, string-pulled) ─────────────────────────────

export interface Obstacle { x: number; z: number; hx: number; hz: number }
export interface PathOptions { cell?: number; radius?: number; agent?: number; maxNodes?: number }

/**
 * Find a walkable route from a → b around axis-aligned obstacles (build parts,
 * placements, NPCs treat each other as soft). Grid cells of `cell` metres over
 * a window of ±`radius` around the start; obstacles are inflated by `agent`
 * (the walker's half-width) so paths never hug a wall; 8-connected moves with
 * corner cutting forbidden; the result is string-pulled (any two points with
 * clear line of sight are merged). Returns [] when unreachable.
 */
export function findPath(a: { x: number; z: number }, b: { x: number; z: number }, obstacles: Obstacle[], opts: PathOptions = {}): Array<{ x: number; z: number }> {
  const cell = opts.cell ?? 1, radius = opts.radius ?? 64, agent = opts.agent ?? 0.45, maxNodes = opts.maxNodes ?? 20_000;
  const n = Math.ceil((radius * 2) / cell) + 1;
  const ox = a.x - radius, oz = a.z - radius;
  const inflated = obstacles.map((o) => ({ x: o.x, z: o.z, hx: o.hx + agent, hz: o.hz + agent }));
  const blocked = (gx: number, gz: number): boolean => {
    const wx = ox + gx * cell, wz = oz + gz * cell;
    for (const o of inflated) if (Math.abs(wx - o.x) <= o.hx && Math.abs(wz - o.z) <= o.hz) return true;
    return false;
  };
  const toG = (p: { x: number; z: number }): [number, number] => [Math.round((p.x - ox) / cell), Math.round((p.z - oz) / cell)];
  const [sx, sz] = toG(a);
  let [tx, tz] = toG(b);
  tx = Math.min(n - 1, Math.max(0, tx)); tz = Math.min(n - 1, Math.max(0, tz));
  if (sx < 0 || sz < 0 || sx >= n || sz >= n) return [];
  // A blocked goal: walk to the nearest free cell around it.
  if (blocked(tx, tz)) {
    let best: [number, number] | null = null, bd = Infinity;
    for (let r = 1; r <= 6 && !best; r++) for (let dx = -r; dx <= r; dx++) for (let dz = -r; dz <= r; dz++) {
      const gx = tx + dx, gz = tz + dz;
      if (gx < 0 || gz < 0 || gx >= n || gz >= n || blocked(gx, gz)) continue;
      const d = dx * dx + dz * dz;
      if (d < bd) { bd = d; best = [gx, gz]; }
    }
    if (!best) return [];
    [tx, tz] = best;
  }
  const key = (x: number, z: number): number => z * n + x;
  const h = (x: number, z: number): number => { const dx = Math.abs(x - tx), dz = Math.abs(z - tz); return Math.max(dx, dz) + (Math.SQRT2 - 1) * Math.min(dx, dz); };
  const g = new Map<number, number>(); const came = new Map<number, number>();
  const open: Array<{ k: number; x: number; z: number; f: number }> = [{ k: key(sx, sz), x: sx, z: sz, f: h(sx, sz) }];
  g.set(key(sx, sz), 0);
  const closed = new Set<number>();
  let expanded = 0;
  const DIRS: Array<[number, number, number]> = [[1, 0, 1], [-1, 0, 1], [0, 1, 1], [0, -1, 1], [1, 1, Math.SQRT2], [1, -1, Math.SQRT2], [-1, 1, Math.SQRT2], [-1, -1, Math.SQRT2]];
  let found = false;
  while (open.length) {
    // Cheap priority queue: pick the min f (paths are short; n² ≤ ~17k cells).
    let bi = 0;
    for (let i = 1; i < open.length; i++) if (open[i].f < open[bi].f) bi = i;
    const cur = open.splice(bi, 1)[0];
    if (closed.has(cur.k)) continue;
    closed.add(cur.k);
    if (cur.x === tx && cur.z === tz) { found = true; break; }
    if (++expanded > maxNodes) break;
    const gc = g.get(cur.k) ?? Infinity;
    for (const [dx, dz, cost] of DIRS) {
      const nx = cur.x + dx, nz = cur.z + dz;
      if (nx < 0 || nz < 0 || nx >= n || nz >= n || blocked(nx, nz)) continue;
      if (dx !== 0 && dz !== 0 && (blocked(cur.x + dx, cur.z) || blocked(cur.x, cur.z + dz))) continue; // no corner cutting
      const nk = key(nx, nz);
      const ng = gc + cost;
      if (ng < (g.get(nk) ?? Infinity)) { g.set(nk, ng); came.set(nk, cur.k); open.push({ k: nk, x: nx, z: nz, f: ng + h(nx, nz) }); }
    }
  }
  if (!found) return [];
  const cells: Array<[number, number]> = [];
  let k: number | undefined = key(tx, tz);
  while (k !== undefined) { cells.push([k % n, Math.floor(k / n)]); k = came.get(k); }
  cells.reverse();
  const pts = cells.map(([gx, gz]) => ({ x: ox + gx * cell, z: oz + gz * cell }));
  pts[pts.length - 1] = { x: b.x, z: b.z };
  // String pulling: skip intermediate points while the straight line stays clear.
  const clear = (p: { x: number; z: number }, q: { x: number; z: number }): boolean => {
    const steps = Math.ceil(Math.hypot(q.x - p.x, q.z - p.z) / (cell * 0.5));
    for (let i = 1; i < steps; i++) { const t = i / steps; const x = p.x + (q.x - p.x) * t, z = p.z + (q.z - p.z) * t; for (const o of inflated) if (Math.abs(x - o.x) <= o.hx && Math.abs(z - o.z) <= o.hz) return false; }
    return true;
  };
  const out: Array<{ x: number; z: number }> = [pts[0]];
  let i = 0;
  while (i < pts.length - 1) {
    let j = pts.length - 1;
    while (j > i + 1 && !clear(pts[i], pts[j])) j--;
    out.push(pts[j]);
    i = j;
  }
  return out.slice(1);
}
